import { BaseApiService } from './BaseApiService';
import { sharedTokenRefresh } from './tokenRefresh';
import { debugError } from '../utils/debug';

export type NotificationMessageFormat = 'text' | 'html';

export interface NotificationVideoPlayback {
    success: boolean;
    message?: string;
    iframeSrc?: string;
    token?: string;
    expiresAt?: string;
}

export interface NotificationVideoStatus {
    success: boolean;
    message?: string;
    state?: string;
    durationSeconds?: number;
}

export interface NotificationItem {
    notificationId: string;
    userId: string;
    teamId: string;
    title: string;
    message: string;
    /** Missing means 'text'. */
    messageFormat?: NotificationMessageFormat;
    notificationType?: string;
    linkUrl?: string;
    isRead: boolean;
    readAt?: string;
    createdAt: string;
    createdBy: string;
    updatedAt: string;
    updatedBy: string;
}

export interface NotificationListResponse {
    success: boolean;
    message?: string;
    notifications: NotificationItem[];
}

export interface NotificationUnreadCountResponse {
    success: boolean;
    message?: string;
    unreadCount: number;
}

/** Server returns most recent notifications for the full page (see list API). */
export const NOTIFICATIONS_PAGE_TAKE = 50;

/** Max unread rows for bell popdown (explicit take to API). */
export const NOTIFICATION_BELL_UNREAD_TAKE = 12;

export interface ListNotificationsParams {
    unreadOnly?: boolean;
    take?: number;
}

export class NotificationApiService extends BaseApiService {
    constructor() {
        super(sharedTokenRefresh);
    }

    async listNotifications(params: ListNotificationsParams = {}): Promise<NotificationListResponse> {
        try {
            const response = await this.postJson('/v1/notifications/list', {
                unreadOnly: params.unreadOnly,
                take: params.take,
            });
            return await this.handleResponse<NotificationListResponse>(response);
        } catch (error) {
            debugError('Error listing notifications:', error);
            throw error;
        }
    }

    async getUnreadCount(): Promise<NotificationUnreadCountResponse> {
        try {
            const response = await this.postJson('/v1/notifications/unread-count', {});
            return await this.handleResponse<NotificationUnreadCountResponse>(response);
        } catch (error) {
            debugError('Error getting unread count:', error);
            throw error;
        }
    }

    async markAllRead(): Promise<{ success: boolean; markedCount?: number; message?: string }> {
        try {
            const response = await this.postJson('/v1/notifications/mark-all-read', {});
            return await this.handleResponse<{ success: boolean; markedCount?: number; message?: string }>(response);
        } catch (error) {
            debugError('Error marking all notifications read:', error);
            throw error;
        }
    }

    async toggleRead(notificationId: string, isRead: boolean): Promise<{ success: boolean; message?: string }> {
        try {
            const response = await this.postJson('/v1/notifications/toggle-read', {
                notificationId,
                isRead
            });
            return await this.handleResponse<{ success: boolean; message?: string }>(response);
        } catch (error) {
            debugError('Error toggling notification read:', error);
            throw error;
        }
    }

    async adminListNotifications(userId?: string, teamId?: string): Promise<NotificationListResponse> {
        try {
            const response = await this.postJson('/v1/notifications/admin/list', { userId, teamId });
            return await this.handleResponse<NotificationListResponse>(response);
        } catch (error) {
            debugError('Error listing all notifications (admin):', error);
            throw error;
        }
    }

    async adminSendNotification(userId: string, teamId: string, title: string, message: string, notificationType?: string, linkUrl?: string, messageFormat: NotificationMessageFormat = 'text'): Promise<{ success: boolean; message?: string }> {
        try {
            const response = await this.postJson('/v1/notifications/admin/send', {
                userId,
                teamId,
                title,
                message,
                notificationType,
                linkUrl,
                messageFormat
            });
            return await this.handleResponse<{ success: boolean; message?: string }>(response);
        } catch (error) {
            debugError('Error sending notification (admin):', error);
            throw error;
        }
    }

    async adminSendBulkNotification(params: {
        recipientMode: string;
        userId?: string;
        teamId?: string;
        userIds?: string[];
        teamIds?: string[];
        title: string;
        message: string;
        messageFormat?: NotificationMessageFormat;
        notificationType?: string;
        linkUrl?: string;
    }): Promise<{ success: boolean; sentCount?: number; message?: string }> {
        try {
            const response = await this.postJson('/v1/notifications/admin/send-bulk', params);
            return await this.handleResponse<{ success: boolean; sentCount?: number; message?: string }>(response);
        } catch (error) {
            debugError('Error sending bulk notification (admin):', error);
            throw error;
        }
    }

    async adminUploadImage(file: File): Promise<{ success: boolean; url?: string; message?: string }> {
        try {
            const fd = new FormData();
            fd.append('file', file);
            const response = await this.postFormData('/v1/admin/notification-media/image', fd);
            return await this.handleResponse<{ success: boolean; url?: string; message?: string }>(response);
        } catch (error) {
            debugError('Error uploading notification image:', error);
            throw error;
        }
    }

    async adminCreateVideoUpload(): Promise<{ success: boolean; uploadUrl?: string; videoUid?: string; message?: string }> {
        try {
            const response = await this.postJson('/v1/admin/notification-media/video/create-upload', {});
            return await this.handleResponse<{ success: boolean; uploadUrl?: string; videoUid?: string; message?: string }>(response);
        } catch (error) {
            debugError('Error creating notification video upload:', error);
            throw error;
        }
    }

    async adminGetVideoStatus(videoUid: string): Promise<NotificationVideoStatus> {
        try {
            const response = await this.postJson('/v1/admin/notification-media/video/status', { videoUid });
            return await this.handleResponse<NotificationVideoStatus>(response);
        } catch (error) {
            debugError('Error getting notification video status:', error);
            throw error;
        }
    }

    async adminGetVideoPreview(videoUid: string): Promise<NotificationVideoPlayback> {
        try {
            const response = await this.postJson('/v1/admin/notification-media/video/preview', { videoUid });
            return await this.handleResponse<NotificationVideoPlayback>(response);
        } catch (error) {
            debugError('Error getting notification video preview:', error);
            throw error;
        }
    }

    async getVideoPlayback(notificationId: string, videoUid: string): Promise<NotificationVideoPlayback> {
        try {
            const response = await this.postJson('/v1/notifications/video-playback', { notificationId, videoUid });
            return await this.handleResponse<NotificationVideoPlayback>(response);
        } catch (error) {
            debugError('Error getting notification video playback:', error);
            throw error;
        }
    }
}

export const notificationApiService = new NotificationApiService();
